import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { imageApi, itemApi } from '../../lib/api'

function parseEurosToCents(raw: string): number {
  const n = Number.parseFloat(raw.replace(',', '.'))
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100)
}

function isProbablyHttpUrl(s: string): boolean {
  try {
    const u = new URL(s)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

function formatEuroFromCents(cents: number): string {
  const euros = cents / 100
  return euros.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function ResponsiveSheet({
  open,
  onClose,
  ariaLabel,
  children,
}: {
  open: boolean
  onClose: () => void
  ariaLabel: string
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      className="if-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="if-sheet" role="dialog" aria-modal="true" aria-label={ariaLabel}>
        {children}
      </div>
    </div>
  )
}

export default function ItemForm() {
  const { rid, iid } = useParams<{ rid: string; iid?: string }>()
  const nav = useNavigate()
  const editing = Boolean(iid)

  const [externalUrl, setExternalUrl] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priceEuros, setPriceEuros] = useState('')

  const [autoState, setAutoState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle')
  const [autoMsg, setAutoMsg] = useState<string | null>(null)

  const [candidates, setCandidates] = useState<string[]>([])
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null)

  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadPreview, setUploadPreview] = useState<string | null>(null)
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null)
  const [replaceMode, setReplaceMode] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [touched, setTouched] = useState<{ url?: boolean; title?: boolean; price?: boolean }>({})

  const modified = useRef({
    title: false,
    description: false,
    price: false,
  })

  const closeToRegistry = useCallback(() => {
    nav(`/me/r/${rid}`)
  }, [nav, rid])

  useEffect(() => {
    if (!rid || !iid) return
    itemApi
      .list(rid)
      .then((all) => {
        const it = all.find((x) => x.id === iid)
        if (!it) {
          nav(`/me/r/${rid}`)
          return
        }
        setExternalUrl(it.externalUrl ?? '')
        setTitle(it.title)
        setDescription(it.description ?? '')
        setPriceEuros(formatEuroFromCents(it.priceCents))
        setExistingImageUrl(it.imageUrl)
        modified.current = {
          title: Boolean(it.title),
          description: Boolean(it.description),
          price: it.priceCents > 0,
        }
      })
      .catch(() => {
        nav(`/me/r/${rid}`)
      })
  }, [iid, nav, rid])

  async function autofill() {
    if (!rid || !externalUrl) return
    setAutoState('loading')
    setAutoMsg('Recherche en cours sur la page produit…')
    try {
      const r = await itemApi.fromUrl(rid, externalUrl)
      if (!modified.current.title && !title.trim() && r.title) setTitle(r.title)
      if (!modified.current.description && !description.trim() && r.description)
        setDescription(r.description ?? '')
      if (!modified.current.price && !priceEuros.trim() && r.priceCents != null)
        setPriceEuros(formatEuroFromCents(r.priceCents))
      if (r.externalUrl) setExternalUrl(r.externalUrl)
      setCandidates(r.imageCandidates)
      if (!uploadFile && !existingImageUrl) setSelectedCandidate(r.imageCandidates[0] ?? null)
      setAutoState('ok')
      setAutoMsg(
        `${r.imageCandidates.length} images trouvées · champs remplis (vos modifications sont préservées).`,
      )
    } catch {
      setAutoState('error')
      setAutoMsg(
        'Auto-remplissage indisponible pour cette URL — vous pouvez remplir les champs manuellement.',
      )
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!rid) return
    setSubmitting(true)
    setTouched({ url: true, title: true, price: true })
    try {
      let imageId: string | null = null
      if (uploadFile) {
        const r = await imageApi.upload(uploadFile, rid)
        imageId = r.id
      } else if (selectedCandidate) {
        const r = await imageApi.fromUrl(selectedCandidate, rid)
        imageId = r.id
      }

      const priceCents = parseEurosToCents(priceEuros)
      const payload = {
        title,
        description: description || null,
        priceCents,
        externalUrl: externalUrl.trim() || null,
        imageId,
      }

      if (editing) await itemApi.update(rid, iid!, payload)
      else await itemApi.create(rid, payload)

      nav(`/me/r/${rid}`)
    } catch {
      setSubmitting(false)
    }
  }

  const urlValid = isProbablyHttpUrl(externalUrl)
  const titleValid = title.trim().length > 0
  const priceValid = parseEurosToCents(priceEuros) > 0
  const canSave = (!externalUrl.trim() || urlValid) && titleValid && priceValid && !submitting

  const previewUrl = useMemo(() => {
    if (uploadPreview) return uploadPreview
    if (selectedCandidate) return selectedCandidate
    if (!replaceMode && existingImageUrl) return existingImageUrl
    return null
  }, [existingImageUrl, replaceMode, selectedCandidate, uploadPreview])

  async function onFile(file?: File) {
    if (!file) return
    setUploadFile(file)
    setSelectedCandidate(null)
    setReplaceMode(false)
    setExistingImageUrl(null)
    const reader = new FileReader()
    reader.onload = () => setUploadPreview(String(reader.result))
    reader.readAsDataURL(file)
  }

  return (
    <div className="owner-item-form">
      <ResponsiveSheet
        open
        ariaLabel={editing ? 'Modifier le cadeau' : 'Ajouter un cadeau'}
        onClose={closeToRegistry}
      >
        <form className="if-form" onSubmit={onSubmit} noValidate>
          <header className="if-head">
            <div>
              <p className="eyebrow if-eyebrow">
                {editing ? 'Modifier le cadeau' : 'Nouveau cadeau'}
              </p>
              <h2 className="if-title">
                {editing ? 'Modifier ce cadeau' : 'Ajoutez un cadeau à la liste'}
              </h2>
            </div>
            <button
              type="button"
              className="if-close"
              aria-label="Fermer"
              onClick={closeToRegistry}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path
                  d="M6 6l12 12M6 18L18 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </header>

          <div className="if-body">
            <div className="field">
              <label htmlFor="if-url" className="lbl">
                URL du produit <span className="muted">(facultatif)</span>
              </label>
              <div className="if-url-row">
                <input
                  id="if-url"
                  type="url"
                  inputMode="url"
                  autoComplete="url"
                  className={`input ${touched.url && !urlValid ? 'input-error' : ''}`}
                  placeholder="https://www.boutique.com/poussette-yoyo"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, url: true }))}
                  aria-invalid={Boolean(touched.url && !urlValid)}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="btn-secondary if-autofill"
                  onClick={autofill}
                  disabled={
                    !isProbablyHttpUrl(externalUrl) || autoState === 'loading' || submitting
                  }
                >
                  {autoState === 'loading' ? (
                    <span className="btn-spin">
                      <span className="mini-spin small" aria-hidden="true" /> Analyse…
                    </span>
                  ) : (
                    <>
                      Auto-remplir <span aria-hidden="true">→</span>
                    </>
                  )}
                </button>
              </div>

              {touched.url && !urlValid && externalUrl && (
                <span className="field-error">Cette URL ne semble pas valide.</span>
              )}
              {!touched.url && !externalUrl && (
                <span className="hint">
                  Collez l&apos;adresse d&apos;une page produit pour pré-remplir les champs
                  ci-dessous.
                </span>
              )}
              <div className="if-aria-live" aria-live="polite">
                {autoState === 'loading' && 'Auto-remplissage en cours…'}
                {autoState === 'ok' && 'Champs auto-remplis.'}
                {autoState === 'error' && 'Auto-remplissage indisponible.'}
              </div>

              {autoState === 'ok' && autoMsg && (
                <p className="if-notice if-notice-ok">
                  <span className="if-notice-mark" aria-hidden="true">
                    ✓
                  </span>
                  {autoMsg}
                </p>
              )}
              {autoState === 'error' && autoMsg && (
                <p className="if-notice if-notice-err" role="alert">
                  <span className="if-notice-mark" aria-hidden="true">
                    !
                  </span>
                  {autoMsg}
                </p>
              )}
            </div>

            <div className="field">
              <label htmlFor="if-title" className="lbl">
                Titre
              </label>
              <input
                id="if-title"
                type="text"
                className={`input ${touched.title && !titleValid ? 'input-error' : ''}`}
                value={title}
                onChange={(e) => {
                  modified.current.title = true
                  setTitle(e.target.value)
                }}
                onBlur={() => setTouched((t) => ({ ...t, title: true }))}
                placeholder="Poussette Yoyo"
                required
                aria-invalid={Boolean(touched.title && !titleValid)}
                disabled={submitting}
              />
              {touched.title && !titleValid && (
                <span className="field-error">Donnez un titre à ce cadeau.</span>
              )}
            </div>

            <div className="field">
              <label htmlFor="if-desc" className="lbl">
                Description <span className="muted">(optionnel)</span>
              </label>
              <textarea
                id="if-desc"
                className="input if-textarea"
                rows={4}
                maxLength={4000}
                value={description}
                disabled={submitting}
                onChange={(e) => {
                  modified.current.description = true
                  setDescription(e.target.value)
                }}
                placeholder="Couleur, marque, taille, modèle préféré…"
              />
              <span className="hint if-counter">
                <span>Quelques mots aident vos invités à choisir.</span>
                <span className="muted">{description.length} / 4000</span>
              </span>
            </div>

            <div className="field">
              <label htmlFor="if-price" className="lbl">
                Prix
              </label>
              <div className={`if-price-row ${touched.price && !priceValid ? 'input-error' : ''}`}>
                <input
                  id="if-price"
                  type="text"
                  inputMode="decimal"
                  className="input if-price-input"
                  value={priceEuros}
                  disabled={submitting}
                  onChange={(e) => {
                    modified.current.price = true
                    setPriceEuros(e.target.value.replace(/[^\d,.]/g, ''))
                  }}
                  onBlur={() => setTouched((t) => ({ ...t, price: true }))}
                  placeholder="0,00"
                  required
                  aria-invalid={Boolean(touched.price && !priceValid)}
                />
                <span className="if-price-suffix" aria-hidden="true">
                  €
                </span>
              </div>
              {touched.price && !priceValid && (
                <span className="field-error">Indiquez un prix supérieur à zéro.</span>
              )}
              {!touched.price && (
                <span className="hint">
                  Vos invités pourront contribuer à hauteur qu&apos;ils choisissent.
                </span>
              )}
            </div>

            <fieldset className="if-image-block">
              <legend className="lbl">
                Image <span className="muted">(optionnel)</span>
              </legend>

              {editing && existingImageUrl && !replaceMode && !uploadPreview && (
                <div className="if-existing">
                  <div className="if-existing-thumb">
                    <img src={existingImageUrl} alt="Image actuelle du cadeau" />
                  </div>
                  <div className="if-existing-meta">
                    <p className="if-existing-label">Image actuelle</p>
                    <button type="button" className="if-link" onClick={() => setReplaceMode(true)}>
                      Remplacer cette image →
                    </button>
                  </div>
                </div>
              )}

              {candidates.length > 0 && (!existingImageUrl || replaceMode || uploadPreview) && (
                <div className="if-strip-wrap">
                  <p className="if-strip-label">Choisissez une image trouvée sur la page :</p>
                  <div
                    className="if-strip"
                    role="radiogroup"
                    aria-label="Images trouvées sur la page"
                  >
                    {candidates.map((src, i) => {
                      const id = `if-cand-${i}`
                      const checked = selectedCandidate === src && !uploadFile
                      return (
                        <label
                          key={src}
                          htmlFor={id}
                          className={`if-strip-item ${checked ? 'is-on' : ''}`}
                        >
                          <input
                            id={id}
                            type="radio"
                            name="if-candidate"
                            className="if-strip-radio"
                            checked={checked}
                            disabled={submitting}
                            onChange={() => {
                              setSelectedCandidate(src)
                              setUploadFile(null)
                              setUploadPreview(null)
                            }}
                          />
                          <img src={src} alt={`Image option ${i + 1}`} />
                          <span className="if-strip-num" aria-hidden="true">
                            {i + 1}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}

              {candidates.length === 0 && (!existingImageUrl || replaceMode) && (
                <p className="if-strip-label if-strip-label-empty">
                  Aucune image trouvée — vous pouvez en téléverser une.
                </p>
              )}

              {(!existingImageUrl || replaceMode) && (
                <div className="if-upload-row">
                  <span className="if-or-divider">
                    <span>Ou</span>
                  </span>
                  <label className="if-upload">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/avif"
                      className="sr-only"
                      disabled={submitting}
                      onChange={(e) => void onFile(e.target.files?.[0] ?? undefined)}
                    />
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                      <path
                        d="M12 16V5M7 10l5-5 5 5M5 19h14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Téléverser depuis votre appareil</span>
                  </label>
                  {replaceMode && existingImageUrl && (
                    <button
                      type="button"
                      className="if-link if-link-quiet"
                      disabled={submitting}
                      onClick={() => {
                        setReplaceMode(false)
                        setUploadFile(null)
                        setUploadPreview(null)
                        setSelectedCandidate(null)
                      }}
                    >
                      Conserver l&apos;image actuelle
                    </button>
                  )}
                </div>
              )}

              {previewUrl && (
                <figure className="if-preview">
                  <img src={previewUrl} alt="" />
                  <figcaption className="if-preview-cap">
                    {uploadFile ? (
                      <>
                        <em>Aperçu du fichier :</em> {uploadFile.name}
                      </>
                    ) : selectedCandidate ? (
                      <em>Image sélectionnée depuis la page produit</em>
                    ) : (
                      <em>Image actuelle</em>
                    )}
                    {(uploadFile || selectedCandidate) && (
                      <button
                        type="button"
                        className="if-link if-link-quiet if-preview-clear"
                        disabled={submitting}
                        onClick={() => {
                          setUploadFile(null)
                          setUploadPreview(null)
                          setSelectedCandidate(null)
                        }}
                      >
                        Retirer
                      </button>
                    )}
                  </figcaption>
                </figure>
              )}
            </fieldset>
          </div>

          <footer className="if-foot">
            <button
              type="button"
              className="btn-ghost"
              onClick={closeToRegistry}
              disabled={submitting}
            >
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={!canSave}>
              {submitting ? (
                <span className="btn-spin">
                  <span className="mini-spin" aria-hidden="true" /> Enregistrement…
                </span>
              ) : editing ? (
                'Enregistrer les modifications'
              ) : (
                'Ajouter à la liste'
              )}
            </button>
          </footer>
        </form>
      </ResponsiveSheet>
    </div>
  )
}
